/* -----------------Child components--------------- */
import { appBarMeta } from "@constants/appbar";
import { events, pageViewTypes } from "@constants/tracking";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { FeedbackDialog } from "@features/feedback/components/dialogs/FeedbackDialog";
import { defaultFeedbackSubtitle } from "@features/feedback/constants";
import { SessionConfigDialog } from "@features/session-config/components/SessionConfigDialog";
import { getShowStudyPackViews } from "@helpers/exams";
/* -----------------Features--------------- */
import { isWeb } from "@helpers/platform";
import { getProgressCardsData } from "@helpers/progress-data";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import { useDevice } from "@hooks/useDevice";
import useMixedItemDist from "@hooks/useMixedItemDist";
import ProgressApplet from "@memorang/ui/src/components/ProgressApplet";
import { trackCustomEvent } from "analytics";
import DocumentTitle from "components/DocumentTitle";
import { router, usePathname } from "expo-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ProgressLoadingContainer from "./ProgressLoadingContainer";

const ProgressContainer = () => {
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);
	const { handleFeedbackNavigation, handleSessionConfigNavigation } =
		useCustomNavigation();
	const { newUserInfo } = useAuthContext();
	const pathname = usePathname();
	const { t } = useTranslation();
	const appFont = useAppStore((store) => store.app.font) || "opensans";
	const showPracticeTests = useAppStore((state) => state.app.showPracticeTests);
	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();
	const { data: mixedItemDist, isLoading } = useMixedItemDist();
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const memoizedAnalyticsParams = useMemo(() => {
		return {
			type: pageViewTypes.progress,
		};
	}, []);

	useAnalytics(memoizedAnalyticsParams);

	if (isLoading || !mixedItemDist) {
		return (
			<ProgressLoadingContainer
				onFeedbackToggle={() => setShowFeedbackDialog((prev) => !prev)}
			/>
		);
	}

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);
	const finalHeaderSubtitle = isMobile ? "" : appBarMeta.progress.description;

	const {
		masteryTagsProgressData,
		needsImprovementProgressData,
		progressPageSummaryCardData,
		practiceTestsCardsData,
		showEmptyState,
	} = getProgressCardsData(mixedItemDist!, "Progress", true, t, theme);

	const weakTagIds = needsImprovementProgressData
		? needsImprovementProgressData.items.map((item) => item.id!)
		: [];

	const handleNavigation = (numericId: number) => {
		router.push(`/session/${numericId}/report/`);
	};

	const toggleSessionConfigDialog = () => {
		setShowSessionConfigDialog(!showSessionConfigDialog);
	};

	const toggleShowFeedbackDialog = () => {
		setShowFeedbackDialog((prev) => !prev);
	};

	const handleClickInfo = (text: string, title: string) => {
		router.push({
			pathname: "/info",
			params: {
				body: text,
				title: title,
			},
		});
	};

	const handleClickStudy = () => {
		const tagIds = needsImprovementProgressData
			? needsImprovementProgressData.items.map((item) => item.id!)
			: [];
		handleSessionConfigNavigation(toggleSessionConfigDialog, {
			selectedTagIds: tagIds,
			reset: "true",
			studyRecommenedTopics: "true",
		});
	};

	const handleClickCard = (title: string) => {
		router.push({
			pathname: "/progress/detailed-list/mastery",
			params: {
				type: title,
			},
		});
	};

	return (
		<>
			<DocumentTitle title="Progress" />
			<ProgressApplet
				showPracticeTests={showPracticeTests}
				handleClickCard={handleClickCard}
				handleClickInfo={handleClickInfo}
				showStudyPackViews={showStudyPackViews}
				handleClickStudy={handleClickStudy}
				practiceTestsCardsData={practiceTestsCardsData}
				progressPageSummaryCardData={progressPageSummaryCardData}
				appFont={appFont}
				handleNavigation={handleNavigation}
				progressNeedsImprovementCtaLabel={t("progress.needsImprovement.cta")}
				emptyState={{
					title: t("emptyState.progress.title"),
					subtitle: t("emptyState.progress.subtitle"),
					cta: t("emptyState.progress.cta"),
					onAction: () => {
						if (showStudyPackViews) {
							handleSessionConfigNavigation(toggleSessionConfigDialog);
						} else {
							router.push("/home");
						}
					},
				}}
				masteryTagsProgressData={masteryTagsProgressData}
				needsImprovementProgressData={needsImprovementProgressData}
				showEmptyState={showEmptyState}
				weakTagIds={weakTagIds.map((id) => id.toString())}
				CustomAppBar={{
					handleTrackEvent: () => {
						trackCustomEvent({
							eventName: events.commandBarClicked,
							option: "command_bar",
						});
					},
					handleFeedbackNavigation: () =>
						handleFeedbackNavigation(toggleShowFeedbackDialog),
					back: false,
					onBackPress: () => {
						if (router.canGoBack()) {
							router.back();
						} else {
							router.push("/(protected)/(tabs)/home");
						}
					},
					onAvatarPress: () => router.push("/(protected)/account/settings"),
					userInfo: {
						firstName: newUserInfo?.firstName || newUserInfo?.name,
						lastName: newUserInfo?.lastName,
					},
					showHambugerMenu: true,
					showCommandBar: isWeb,
					role: "banner",
					options: {
						headerTitle: t(appBarMeta.progress.title),
						subtitle: finalHeaderSubtitle,
					},
				}}
			/>
			{showSessionConfigDialog && weakTagIds ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					handleClose={toggleSessionConfigDialog}
				/>
			) : null}
			{showSessionConfigDialog && !weakTagIds ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					handleClose={toggleSessionConfigDialog}
				/>
			) : null}
			{isWeb && (
				<FeedbackDialog
					visible={showFeedbackDialog}
					view={pathname}
					feedbackSubtitle={t(defaultFeedbackSubtitle)}
					handleDismiss={toggleShowFeedbackDialog}
				/>
			)}
		</>
	);
};

export default ProgressContainer;
